/**
 * Store-once (content-addressed) deploy service for HTML sites.
 *
 * Given a flat file list ({ path, file }), this:
 *   1. Hashes each file with SHA-256 and builds a manifest { path: hash }.
 *   2. Uploads only the blobs not already present at blobs/{uid}/{hash}
 *      (concurrency-limited, retried), each tagged with a path-derived MIME.
 *   3. Diffs against the previous active version to compute a change summary.
 *   4. Atomically publishes: writes the immutable version doc, then (if makeLive)
 *      flips the url doc's activeVersion — never before all uploads succeed.
 *
 * OWNED sites: all writes go through the client SDK under the owner-scoped
 * rules; there is no server endpoint and no admin credentials in the browser.
 *
 * SHARED sites (admin site-sharing: the signed-in user is a collaborator in
 * the owner's url doc sharedWith array, deploying into the OWNER's namespace):
 * Storage rules only match the owner's uid, so the blob existence check and
 * the blob uploads route through /api/deploy-upload-urls instead (Firebase
 * ID-token auth, owner-or-sharedWith authorization, short-lived signed PUT
 * URLs scoped to the exact blobs/{ownerUid}/{hash} objects). The Firestore
 * publish steps (version doc, activeVersion flip) stay client-side exactly as
 * for owned sites — firestore.rules allows collaborators there — so the
 * published version shape is identical on both paths.
 */
import firebase, { storage, auth } from '../../firebase';
import { ref, uploadBytesResumable, getMetadata } from 'firebase/storage';
import pLimit from 'p-limit';
import mime from 'mime';
import { startTrialClock } from '../trialClock';

const UPLOAD_CONCURRENCY = 5;
const MAX_ATTEMPTS = 3;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// One-shot SHA-256 over the whole file; returns lowercase hex + byte size.
async function hashFile(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return { hash: hex, size: buf.byteLength };
}

// Derive Content-Type from the LOGICAL path, never File.type (unreliable for
// folder/zip uploads). Fall back to a safe binary type.
function contentTypeForPath(path) {
  return mime.getType(path) || 'application/octet-stream';
}

// getMetadata throws object-not-found when the blob is absent; treat that as
// "not present" and let any other error bubble up.
async function blobExists(uid, hash) {
  try {
    await getMetadata(ref(storage, `blobs/${uid}/${hash}`));
    return true;
  } catch (err) {
    if (err && err.code === 'storage/object-not-found') return false;
    throw err;
  }
}

async function withRetry(fn) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_ATTEMPTS - 1) await sleep(500 * 2 ** attempt);
    }
  }
  throw lastErr;
}

// A deploy is a COLLABORATOR deploy when it targets another user's namespace
// (shared site: `uid` is the owner, the signed-in user is a sharedWith
// collaborator). Falls back to the owned-site path when auth hasn't resolved,
// which matches today's behaviour exactly.
function isCollaboratorDeploy(uid) {
  const current = auth.currentUser;
  return !!current && current.uid !== uid;
}

// Mint signed upload URLs for a shared-site deploy. The server
// (api/deploy-upload-urls.js, Firebase ID-token mode) authorizes the caller
// against user/{ownerUid}/url/{urlId} (owner-or-sharedWith), runs the blob
// existence check with the Admin SDK — the client cannot read
// blobs/{ownerUid} under Storage rules — and returns one entry per unique
// hash: { hash, url|null, alreadyExists, requiredHeaders }.
async function mintSharedUploadUrls(ownerUid, urlId, files) {
  const current = auth.currentUser;
  if (!current) throw new Error('Not signed in');
  const idToken = await current.getIdToken();
  const res = await fetch('/api/deploy-upload-urls', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ ownerUid, siteId: urlId, files }),
  });
  if (!res.ok) {
    let message = `Could not prepare the shared upload (${res.status})`;
    try {
      const body = await res.json();
      if (body && body.error && body.error.message) message = body.error.message;
    } catch (e) {
      // Non-JSON error body — keep the status-code message.
    }
    throw new Error(message);
  }
  return res.json();
}

// PUT one blob's bytes to its minted signed URL. The exact requiredHeaders
// from the mint response must be sent or the GCS signature will not match.
async function putBlobToSignedUrl(entry, file) {
  const res = await fetch(entry.url, {
    method: 'PUT',
    headers: entry.requiredHeaders || {},
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Shared blob upload failed (${res.status})`);
  }
}

// Upload a single blob, reporting absolute bytesTransferred for this task.
function uploadBlob(uid, hash, file, contentType, onBytes) {
  const blobRef = ref(storage, `blobs/${uid}/${hash}`);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(blobRef, file, { contentType });
    task.on(
      'state_changed',
      (snap) => onBytes && onBytes(snap.bytesTransferred),
      reject,
      resolve
    );
  });
}

// Build { added, changed, removed } by diffing newManifest against the
// currently-active version's manifest (if any).
async function computeChangeSummary(db, uid, urlId, newManifest) {
  const urlSnap = await db
    .collection('user').doc(uid)
    .collection('url').doc(urlId)
    .get();
  const prevVersionId = urlSnap.exists ? urlSnap.data().activeVersion : null;

  let prevManifest = {};
  if (prevVersionId) {
    const prevSnap = await db
      .collection('user').doc(uid)
      .collection('url').doc(urlId)
      .collection('versions').doc(prevVersionId)
      .get();
    if (prevSnap.exists) prevManifest = prevSnap.data().files || {};
  }

  const added = [];
  const changed = [];
  const removed = [];
  for (const path of Object.keys(newManifest)) {
    if (!(path in prevManifest)) added.push(path);
    else if (prevManifest[path] !== newManifest[path]) changed.push(path);
  }
  for (const path of Object.keys(prevManifest)) {
    if (!(path in newManifest)) removed.push(path);
  }
  return { added, changed, removed };
}

/**
 * Deploy a gathered file list to a site.
 *
 * @param {Object}   opts
 * @param {string}   opts.uid         Owner uid.
 * @param {string}   opts.urlId       url doc id (the site).
 * @param {Array}    opts.files       [{ path, file }] from gatherFiles.
 * @param {string}   opts.homePage    Logical homepage path.
 * @param {string}   [opts.serveMode] 'multipage' (default) | 'spa'.
 * @param {boolean}  [opts.makeLive]  Flip activeVersion on success (default true).
 * @param {Function} [opts.onProgress] Progress callback (see below).
 * @returns {Promise<Object>} deploy result summary.
 */
export async function deploySite({
  uid,
  urlId,
  files,
  homePage,
  serveMode = 'multipage',
  makeLive = true,
  onProgress,
}) {
  if (!uid) throw new Error('Missing uid');
  if (!urlId) throw new Error('Missing site id');
  if (!files || files.length === 0) throw new Error('No files to deploy');

  const db = firebase.firestore();

  // --- 1. Hash everything, build manifest + dedupe by hash. ---
  if (onProgress) onProgress({ phase: 'hashing', hashed: 0, total: files.length });
  const manifest = {};
  const sizes = {}; // logical path -> bytes, written alongside the manifest
  const byHash = new Map(); // hash -> { file, path, size }
  let totalBytes = 0;
  let hashed = 0;
  for (const { path, file } of files) {
    const { hash, size } = await hashFile(file);
    manifest[path] = hash;
    sizes[path] = size;
    totalBytes += size;
    if (!byHash.has(hash)) byHash.set(hash, { file, path, size });
    hashed++;
    if (onProgress) onProgress({ phase: 'hashing', hashed, total: files.length });
  }

  // --- 2. Determine which unique blobs are absent, upload only those. ---
  // Owned sites keep the original client-SDK path untouched; shared sites
  // route the existence check AND the uploads through the server signed-URL
  // flow (see the module header).
  const uniqueHashes = [...byHash.keys()];
  const collaborator = isCollaboratorDeploy(uid);
  let toUpload;
  let totalUploadBytes;

  let uploadedBytes = 0;
  let blobsUploaded = 0;
  const report = () => {
    if (!onProgress) return;
    onProgress({
      phase: 'uploading',
      uploadedBytes,
      totalUploadBytes,
      blobsUploaded,
      blobsTotal: toUpload.length,
    });
  };

  if (collaborator) {
    const { uploadUrls } = await mintSharedUploadUrls(
      uid,
      urlId,
      uniqueHashes.map((h) => ({
        hash: h,
        size: byHash.get(h).size,
        path: byHash.get(h).path,
      }))
    );
    const urlByHash = new Map((uploadUrls || []).map((u) => [u.hash, u]));
    toUpload = uniqueHashes.filter((h) => {
      const entry = urlByHash.get(h);
      return !!(entry && !entry.alreadyExists && entry.url);
    });
    totalUploadBytes = toUpload.reduce((sum, h) => sum + byHash.get(h).size, 0);
    report();

    const limit = pLimit(UPLOAD_CONCURRENCY);
    await Promise.all(
      toUpload.map((hash) =>
        limit(async () => {
          const { file } = byHash.get(hash);
          await withRetry(() => putBlobToSignedUrl(urlByHash.get(hash), file));
          // Signed-URL PUTs expose no incremental progress; count the whole
          // blob on completion.
          uploadedBytes += byHash.get(hash).size;
          blobsUploaded++;
          report();
        })
      )
    );
  } else {
    const existence = await Promise.all(
      uniqueHashes.map((h) => withRetry(() => blobExists(uid, h)))
    );
    toUpload = uniqueHashes.filter((_, i) => !existence[i]);
    totalUploadBytes = toUpload.reduce((sum, h) => sum + byHash.get(h).size, 0);

    const taskBytes = Object.create(null); // hash -> last reported bytes
    report();

    const limit = pLimit(UPLOAD_CONCURRENCY);
    await Promise.all(
      toUpload.map((hash) =>
        limit(async () => {
          const { file, path } = byHash.get(hash);
          const contentType = contentTypeForPath(path);
          await withRetry(() => {
            // Reset this task's accounting on each attempt (resumable restarts).
            uploadedBytes -= taskBytes[hash] || 0;
            taskBytes[hash] = 0;
            return uploadBlob(uid, hash, file, contentType, (bytes) => {
              uploadedBytes += bytes - (taskBytes[hash] || 0);
              taskBytes[hash] = bytes;
              report();
            });
          });
          // Ensure this blob counts fully even if no final progress event fired.
          uploadedBytes += byHash.get(hash).size - (taskBytes[hash] || 0);
          taskBytes[hash] = byHash.get(hash).size;
          blobsUploaded++;
          report();
        })
      )
    );
  }

  // --- 3. Change summary vs the previous active version. ---
  const changeSummary = await computeChangeSummary(db, uid, urlId, manifest);

  // --- 4. Atomic publish (only reached if every upload above resolved). ---
  if (onProgress) onProgress({ phase: 'publishing' });
  const versionRef = db
    .collection('user').doc(uid)
    .collection('url').doc(urlId)
    .collection('versions').doc();
  const versionId = versionRef.id;

  await versionRef.set({
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    files: manifest,
    sizes,
    fileCount: files.length,
    totalBytes,
    changeSummary,
    homePage,
    serveMode,
    source: 'web',
    // How this deploy happened: 'upload' = browser drop. Additive stamp read by
    // the admin's per-site deploy-method badge; existing sites show "unknown".
    deployMethod: 'upload',
  });

  // Was this site already live before this deploy? (a re-deploy is never a
  // "first publish" for the trial clock.) If the read fails, say yes: not
  // starting a clock is the safe side.
  let wasLive = false;
  if (makeLive) {
    try {
      const before = await db.collection('user').doc(uid).collection('url').doc(urlId).get();
      wasLive = !!(before.exists && before.data().activeVersion);
    } catch (err) {
      wasLive = true;
    }
  }

  if (makeLive) {
    await db
      .collection('user').doc(uid)
      .collection('url').doc(urlId)
      .set(
        {
          type: 'html',
          activeVersion: versionId,
          homePage,
          serveMode,
          // Mirror the deploy method onto the url doc so the admin can badge a
          // site without reading its version subcollection.
          deployMethod: 'upload',
          // Going live clears any creation-draft flag (a deployed site is, by
          // definition, not a draft). No-op for sites that never had one.
          draft: firebase.firestore.FieldValue.delete(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
  }

  // The first site to go live starts the free plan's 7 day clock (no-op for
  // everyone else; utils/trialClock.js). Owners only: a collaborator cannot
  // write the owner's user doc. Best effort, never blocks the deploy.
  if (makeLive && !collaborator) {
    await startTrialClock(db, uid, urlId, { wasLive });
  }

  // Track per-user storage usage by the bytes ACTUALLY uploaded (new blobs
  // only). Collaborators skip this: firestore.rules keeps user/{ownerUid}
  // itself owner-only writable (sharing covers url docs and versions, not the
  // owner's user doc), so the write would be denied and fail the deploy after
  // it already published. Accounting drift is metric-only — htmlStorageBytes
  // is never enforced against a plan.
  if (!collaborator && totalUploadBytes > 0) {
    await db
      .collection('user').doc(uid)
      .set(
        { htmlStorageBytes: firebase.firestore.FieldValue.increment(totalUploadBytes) },
        { merge: true }
      );
  }

  if (onProgress) onProgress({ phase: 'done' });

  return {
    versionId,
    fileCount: files.length,
    totalBytes,
    bytesUploaded: totalUploadBytes,
    blobsUploaded: toUpload.length,
    changeSummary,
    homePage,
    serveMode,
    madeLive: makeLive,
  };
}

/**
 * Fork an existing version with exactly one file replaced.
 *
 * Reads the base version doc, hashes the replacement file, uploads its blob
 * only if absent, and writes a NEW version doc whose manifest is the base
 * manifest with replacePath pointing at the new hash. The result is always a
 * draft: activeVersion is never flipped here, publishing happens from the
 * History card on the site detail page.
 *
 * The change summary is computed against the BASE version (one changed path),
 * not via computeChangeSummary, which diffs against the active version and
 * would be the wrong baseline for a fork.
 *
 * @param {Object} opts
 * @param {string} opts.uid           Owner uid.
 * @param {string} opts.urlId         url doc id (the site).
 * @param {string} opts.baseVersionId Version to fork from.
 * @param {string} opts.replacePath   Logical path being replaced.
 * @param {File}   opts.file          The replacement file.
 * @returns {Promise<string>} the new version id.
 */
export async function deployForkedVersion({ uid, urlId, baseVersionId, replacePath, file }) {
  if (!uid) throw new Error('Missing uid');
  if (!urlId) throw new Error('Missing site id');
  if (!baseVersionId) throw new Error('Missing base version id');
  if (!replacePath) throw new Error('Missing path to replace');
  if (!file) throw new Error('Missing file');

  const db = firebase.firestore();

  const baseSnap = await db
    .collection('user').doc(uid)
    .collection('url').doc(urlId)
    .collection('versions').doc(baseVersionId)
    .get();
  if (!baseSnap.exists) throw new Error('Base version not found');
  const base = baseSnap.data();
  const baseFiles = base.files || {};
  if (!(replacePath in baseFiles)) {
    throw new Error('That file is not part of this version');
  }

  const { hash, size } = await hashFile(file);
  // Owned sites keep the original client-SDK existence check + upload; a
  // collaborator forking a SHARED site goes through the server signed-URL
  // flow, which also performs the existence check (see the module header).
  const collaborator = isCollaboratorDeploy(uid);
  let uploadedNew;
  if (collaborator) {
    const { uploadUrls } = await mintSharedUploadUrls(uid, urlId, [
      { hash, size, path: replacePath },
    ]);
    const entry = uploadUrls && uploadUrls[0];
    if (!entry) throw new Error('Could not prepare the shared upload');
    uploadedNew = !entry.alreadyExists && !!entry.url;
    if (uploadedNew) {
      await withRetry(() => putBlobToSignedUrl(entry, file));
    }
  } else {
    const exists = await withRetry(() => blobExists(uid, hash));
    uploadedNew = !exists;
    if (!exists) {
      await withRetry(() => uploadBlob(uid, hash, file, contentTypeForPath(replacePath)));
    }
  }

  const files = { ...baseFiles, [replacePath]: hash };
  const sizes = base.sizes
    ? { ...base.sizes, [replacePath]: size }
    : { [replacePath]: size };

  // Recompute the total only when the sizes map covers every file; otherwise
  // exact recomputation is impossible, so carry the base total forward.
  const paths = Object.keys(files);
  const sizesComplete = paths.every((p) => typeof sizes[p] === 'number');
  const totalBytes = sizesComplete
    ? paths.reduce((sum, p) => sum + sizes[p], 0)
    : base.totalBytes || 0;

  const changeSummary = { added: [], changed: [replacePath], removed: [] };

  const versionRef = db
    .collection('user').doc(uid)
    .collection('url').doc(urlId)
    .collection('versions').doc();
  await versionRef.set({
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    files,
    sizes,
    fileCount: paths.length,
    totalBytes,
    changeSummary,
    homePage: base.homePage || 'index.html',
    serveMode: base.serveMode || 'multipage',
    source: 'web',
  });

  // Track per-user storage by bytes ACTUALLY uploaded, mirroring deploySite
  // (including its collaborator skip: user/{ownerUid} stays owner-only
  // writable under firestore.rules).
  if (!collaborator && uploadedNew && size > 0) {
    await db
      .collection('user').doc(uid)
      .set(
        { htmlStorageBytes: firebase.firestore.FieldValue.increment(size) },
        { merge: true }
      );
  }

  return versionRef.id;
}
